import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Paperclip, Send, X, Loader2 } from 'lucide-react';
import type { ChatComposerCatalog, ChatToolCatalogItem, ChatToolSet } from '@pm-next/shared-types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FEATURES } from '@/config/features';
import { uploadDocument, createChatAttachment } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import { ChatMentionList } from './ChatMentionList';
import {
  detectComposerTrigger,
  filterComposerToolSets,
  filterComposerTools,
  groupComposerTools,
  insertComposerToken,
  type ComposerTrigger,
} from './composer-mentions';

const MIN_HEIGHT = 40;
const MAX_HEIGHT = 200;

/** Must stay in sync with API `storage.controller` MAX_FILE_SIZE. */
export const CHAT_ATTACH_MAX_BYTES = 20 * 1024 * 1024; // 20 MB

export const ATTACH_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

const ATTACH_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
] as const;

export type ChatSendOptions = {
  attachmentIds?: string[];
  attachments?: Array<{ id: string; fileName: string; mimeType?: string }>;
};

export type ChatInputHandle = {
  attachFiles: (files: FileList | File[]) => Promise<void>;
};

type PendingAttachment = {
  localId: string;
  fileName: string;
  status: 'uploading' | 'processing' | 'ready' | 'error';
  assetId?: string;
  error?: string;
};

interface ChatInputProps {
  onSend: (message: string, options?: ChatSendOptions) => void;
  disabled?: boolean;
  className?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  catalog?: ChatComposerCatalog;
  conversationId?: number | null;
}

export function formatAttachSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const EXT_TO_MIME: Record<(typeof ATTACH_EXTENSIONS)[number], string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
};

/** Resolve a supported MIME, including extension fallback when the OS omits type. */
export function resolveChatAttachMime(file: File): string | null {
  if ((ATTACH_MIMES as readonly string[]).includes(file.type)) return file.type;
  const lower = file.name.toLowerCase();
  for (const ext of ATTACH_EXTENSIONS) {
    if (lower.endsWith(ext)) return EXT_TO_MIME[ext];
  }
  return null;
}

export function isAllowedChatAttachFile(file: File): boolean {
  return resolveChatAttachMime(file) != null;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  { onSend, disabled, className, value: controlledValue, onValueChange, catalog, conversationId },
  ref
) {
  const { toast } = useToast();
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const [cursor, setCursor] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuDismissed, setMenuDismissed] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const pendingCursorRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setValue = useCallback(
    (newValue: string) => {
      if (isControlled) {
        onValueChange?.(newValue);
      } else {
        setInternalValue(newValue);
      }
    },
    [isControlled, onValueChange]
  );

  const trigger = useMemo(() => detectComposerTrigger(value, cursor), [value, cursor]);
  const filteredTools = useMemo(
    () =>
      trigger?.kind === 'tool'
        ? groupComposerTools(filterComposerTools(catalog?.tools ?? [], trigger.query)).flatMap(
            (group) => group.tools
          )
        : [],
    [catalog?.tools, trigger]
  );
  const filteredSets = useMemo(
    () =>
      trigger?.kind === 'set' ? filterComposerToolSets(catalog?.toolSets ?? [], trigger.query) : [],
    [catalog?.toolSets, trigger]
  );
  const catalogReady =
    trigger?.kind === 'tool'
      ? (catalog?.tools.length ?? 0) > 0
      : (catalog?.toolSets.length ?? 0) > 0;
  const menuOpen = Boolean(trigger && catalogReady && !menuDismissed);
  const optionCount = trigger?.kind === 'set' ? filteredSets.length : filteredTools.length;

  const attachmentsBusy = attachments.some(
    (a) => a.status === 'uploading' || a.status === 'processing'
  );
  const readyAttachmentIds = attachments
    .filter((a) => a.status === 'ready' && a.assetId)
    .map((a) => a.assetId!);

  const adjustHeight = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const h = Math.min(Math.max(ta.scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
    ta.style.height = `${h}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  useEffect(() => {
    setActiveIndex(0);
    setMenuDismissed(false);
  }, [trigger?.kind, trigger?.start, trigger?.query]);

  useEffect(() => {
    if (pendingCursorRef.current == null || !textareaRef.current) return;
    const pos = pendingCursorRef.current;
    textareaRef.current.setSelectionRange(pos, pos);
    pendingCursorRef.current = null;
    setCursor(pos);
    textareaRef.current.focus();
  }, [value]);

  const applyToken = useCallback(
    (currentTrigger: ComposerTrigger, token: string) => {
      const { next, cursor: nextCursor } = insertComposerToken(value, currentTrigger, token);
      pendingCursorRef.current = nextCursor;
      setValue(next);
    },
    [setValue, value]
  );

  const selectTool = useCallback(
    (tool: ChatToolCatalogItem) => {
      if (!trigger) return;
      applyToken(trigger, `@${tool.name}`);
    },
    [applyToken, trigger]
  );

  const selectSet = useCallback(
    (toolSet: ChatToolSet) => {
      if (!trigger) return;
      applyToken(trigger, `/${toolSet.id}`);
    },
    [applyToken, trigger]
  );

  const attachFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!FEATURES.chatAttachments) return;
      const list = Array.from(files);
      for (const file of list) {
        const mimeType = resolveChatAttachMime(file);
        if (!mimeType) {
          toast({
            variant: 'destructive',
            title: 'Unsupported file',
            description: `${file.name} — use PDF, Word, text, CSV, or images (JPEG/PNG/WebP).`,
          });
          continue;
        }
        if (file.size > CHAT_ATTACH_MAX_BYTES) {
          toast({
            variant: 'destructive',
            title: 'File too large',
            description: `${file.name} is ${formatAttachSize(file.size)}. Max size is ${formatAttachSize(CHAT_ATTACH_MAX_BYTES)}.`,
          });
          continue;
        }
        if (file.size === 0) {
          toast({
            variant: 'destructive',
            title: 'Empty file',
            description: file.name,
          });
          continue;
        }
        const uploadFile =
          file.type === mimeType ? file : new File([file], file.name, { type: mimeType });
        const localId = crypto.randomUUID();
        setAttachments((prev) => [...prev, { localId, fileName: file.name, status: 'uploading' }]);
        try {
          const uploaded = await uploadDocument(uploadFile);
          setAttachments((prev) =>
            prev.map((a) => (a.localId === localId ? { ...a, status: 'processing' } : a))
          );
          const asset = await createChatAttachment({
            fileName: file.name,
            fileUrl: uploaded.url,
            mimeType,
            conversationId: conversationId ?? undefined,
          });
          setAttachments((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, status: 'ready', assetId: asset.id } : a
            )
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed';
          setAttachments((prev) =>
            prev.map((a) => (a.localId === localId ? { ...a, status: 'error', error: message } : a))
          );
          toast({ variant: 'destructive', title: 'Attachment failed', description: message });
        }
      }
    },
    [conversationId, toast]
  );

  useImperativeHandle(ref, () => ({ attachFiles }), [attachFiles]);

  const removeAttachment = useCallback((localId: string) => {
    setAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!FEATURES.chatAttachments) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        void attachFiles(files);
      }
    },
    [attachFiles]
  );

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if ((!trimmed && readyAttachmentIds.length === 0) || disabled || attachmentsBusy) return;
    onSend(trimmed || 'Please review the attached document(s).', {
      attachmentIds: readyAttachmentIds.length > 0 ? readyAttachmentIds : undefined,
      attachments:
        readyAttachmentIds.length > 0
          ? attachments
              .filter((a) => a.status === 'ready' && a.assetId)
              .map((a) => ({
                id: a.assetId!,
                fileName: a.fileName,
              }))
          : undefined,
    });
    setValue('');
    setAttachments([]);
    setCursor(0);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${MIN_HEIGHT}px`;
    }
    textareaRef.current?.focus();
  }, [value, disabled, attachmentsBusy, readyAttachmentIds, attachments, onSend, setValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (menuOpen && optionCount > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((index) => (index + 1) % optionCount);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((index) => (index - 1 + optionCount) % optionCount);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          if (trigger?.kind === 'set') {
            const toolSet = filteredSets[activeIndex];
            if (toolSet) selectSet(toolSet);
          } else {
            const tool = filteredTools[activeIndex];
            if (tool) selectTool(tool);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMenuDismissed(true);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [
      activeIndex,
      filteredSets,
      filteredTools,
      handleSubmit,
      menuOpen,
      optionCount,
      selectSet,
      selectTool,
      trigger,
    ]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      setCursor(e.target.selectionStart ?? e.target.value.length);
    },
    [setValue]
  );

  const syncCursor = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    setCursor(ta.selectionStart ?? 0);
  }, []);

  const canSend =
    !disabled && !attachmentsBusy && (Boolean(value.trim()) || readyAttachmentIds.length > 0);

  return (
    <div className={cn('relative', className)}>
      {menuOpen && trigger && (
        <div className="absolute bottom-full left-0 right-0 z-20 mb-2">
          <ChatMentionList
            kind={trigger.kind}
            tools={filteredTools}
            toolSets={filteredSets}
            activeIndex={activeIndex}
            onHover={setActiveIndex}
            onSelectTool={selectTool}
            onSelectSet={selectSet}
          />
        </div>
      )}
      <div className="rounded-2xl border bg-background p-2 shadow-md">
        {attachments.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-2 px-1">
            {attachments.map((a) => (
              <li
                key={a.localId}
                className="flex max-w-full items-center gap-1.5 rounded-lg bg-muted px-2 py-1 text-xs"
              >
                {(a.status === 'uploading' || a.status === 'processing') && (
                  <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                )}
                <span className="truncate" title={a.error || a.fileName}>
                  {a.fileName}
                  {a.status === 'processing' ? ' (processing…)' : ''}
                  {a.status === 'error' ? ' (failed)' : ''}
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 hover:bg-background"
                  aria-label={`Remove ${a.fileName}`}
                  onClick={() => removeAttachment(a.localId)}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-end gap-2">
          {FEATURES.chatAttachments && (
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={ATTACH_MIMES.join(',')}
              multiple
              onChange={(e) => {
                if (e.target.files?.length) void attachFiles(e.target.files);
                e.target.value = '';
              }}
            />
          )}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onKeyUp={syncCursor}
            onClick={syncCursor}
            onSelect={syncCursor}
            onPaste={handlePaste}
            placeholder={
              FEATURES.chatAttachments
                ? 'Ask a question…  drop, attach, or paste a file   @ tools   / tool sets'
                : 'Ask a question…  @ tools   / tool sets'
            }
            disabled={disabled}
            rows={1}
            aria-label="Chat message"
            aria-expanded={menuOpen}
            aria-autocomplete="list"
            style={{ minHeight: MIN_HEIGHT, maxHeight: MAX_HEIGHT }}
            className="flex-1 resize-none overflow-y-auto rounded-xl border-0 bg-transparent px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
          {FEATURES.chatAttachments && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled={disabled || attachmentsBusy}
              className="h-9 w-9 shrink-0 rounded-xl"
              aria-label="Attach file"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            size="icon"
            onClick={handleSubmit}
            disabled={!canSend}
            className="h-9 w-9 shrink-0 rounded-xl"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});
